import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { Menu, X, ChevronDown, Globe } from "lucide-react";

const Navbar = () => {
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [travelAgencyOpen, setTravelAgencyOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [internationalOpen, setInternationalOpen] = useState(false);

  const travelRef = useRef(null);
  const langRef = useRef(null);
  const internationalRef = useRef(null);

  const languages = [
    { code: "tr", name: "Türkçe" },
    { code: "en", name: "English" },
    { code: "de", name: "Deutsch" },
    { code: "ru", name: "Русский" },
  ];

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    if (isOpen) {
      return;
    }
    const handleClickOutside = (event) => {
      if (travelRef.current && !travelRef.current.contains(event.target)) {
        setTravelAgencyOpen(false);
      }
      if (langRef.current && !langRef.current.contains(event.target)) {
        setLangOpen(false);
      }
      if (
        internationalRef.current &&
        !internationalRef.current.contains(event.target)
      ) {
        setInternationalOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="container mx-auto px-6">
        <div className="flex justify-between items-center h-28">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 lg:ml-6 xl:ml-10">
            <img
              src="/images/sunkiss_logo.png"
              alt="SunKiss"
              className="h-20 md:h-24 lg:h-28 w-auto transition-all duration-300"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-base font-medium transition-colors ${
                isActive("/")
                  ? "text-amber-500"
                  : "text-gray-700 hover:text-amber-500"
              }`}
            >
              {t("home")}
            </Link>

            {/* Travel Agency Dropdown */}
            <div className="relative" ref={travelRef}>
              <button
                onClick={() => setTravelAgencyOpen(!travelAgencyOpen)}
                className="flex items-center space-x-1 text-base font-medium text-gray-700 hover:text-amber-500 transition-colors"
              >
                <span>{t("travelAgency")}</span>
                <ChevronDown className="h-4 w-4" />
              </button>
              {travelAgencyOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl py-2 border border-gray-100">
                  <Link
                    to="/online-reservation"
                    onClick={() => setTravelAgencyOpen(false)}
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                  >
                    {t("onlineReservation")}
                  </Link>
                  <Link
                    to="/agency-form"
                    onClick={() => setTravelAgencyOpen(false)}
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                  >
                    {t("agencyForm")}
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/hotels"
              className={`text-base font-medium transition-colors ${
                isActive("/hotels")
                  ? "text-amber-500"
                  : "text-gray-700 hover:text-amber-500"
              }`}
            >
              {t("hotels")}
            </Link>

            {/* International Dropdown */}
            <div className="relative" ref={internationalRef}>
              <button
                onClick={() => setInternationalOpen(!internationalOpen)}
                className="flex items-center space-x-1 text-base font-medium text-gray-700 hover:text-amber-500 transition-colors"
              >
                <span>{t("international")}</span>
                <ChevronDown className="h-4 w-4" />
              </button>

              {internationalOpen && (
                <div className="absolute top-full left-0 mt-2 w-44 bg-white rounded-lg shadow-xl py-2 border border-gray-100">
                  <Link
                    to="/dubai"
                    onClick={() => setInternationalOpen(false)}
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                  >
                    {t("dubai")}
                  </Link>
                  <Link
                    to="/cyprus"
                    onClick={() => setInternationalOpen(false)}
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                  >
                    {t("cyprus")}
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/services"
              className={`text-base font-medium transition-colors ${
                isActive("/services")
                  ? "text-amber-500"
                  : "text-gray-700 hover:text-amber-500"
              }`}
            >
              {t("services")}
            </Link>

            <Link
              to="/contact"
              className={`text-base font-medium transition-colors ${
                isActive("/contact")
                  ? "text-amber-500"
                  : "text-gray-700 hover:text-amber-500"
              }`}
            >
              {t("contact")}
            </Link>

            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center space-x-1 text-base font-medium text-gray-700 hover:text-amber-500 transition-colors"
              >
                <Globe className="h-5 w-5" />
                <span className="uppercase">{language}</span>
                <ChevronDown className="h-4 w-4" />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-2 w-40 bg-white rounded-lg shadow-xl py-2 border border-gray-100">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setLangOpen(false);
                      }}
                      className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                        language === lang.code
                          ? "bg-amber-50 text-amber-600 font-medium"
                          : "text-gray-700 hover:bg-amber-50 hover:text-amber-600"
                      }`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-gray-700 hover:text-amber-500 transition-colors"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100">
            <Link
              to="/"
              onClick={() => setIsOpen(false)}
              className="block py-3 text-base font-medium text-gray-700 hover:text-amber-500 transition-colors"
            >
              {t("home")}
            </Link>
            <div className="py-3">
              <button
                onClick={() => setTravelAgencyOpen(!travelAgencyOpen)}
                className="flex items-center justify-between w-full text-base font-medium text-gray-700 hover:text-amber-500 transition-colors"
              >
                <span>{t("travelAgency")}</span>
                <ChevronDown className="h-4 w-4" />
              </button>
              {travelAgencyOpen && (
                <div className="mt-2 ml-4 space-y-2">
                  <Link
                    to="/online-reservation"
                    onClick={() => setIsOpen(false)}
                    className="block py-2 text-sm text-gray-600 hover:text-amber-500 transition-colors"
                  >
                    {t("onlineReservation")}
                  </Link>
                  <Link
                    to="/agency-form"
                    onClick={() => setIsOpen(false)}
                    className="block py-2 text-sm text-gray-600 hover:text-amber-500 transition-colors"
                  >
                    {t("agencyForm")}
                  </Link>
                </div>
              )}
            </div>
            <Link
              to="/hotels"
              onClick={() => setIsOpen(false)}
              className="block py-3 text-base font-medium text-gray-700 hover:text-amber-500 transition-colors"
            >
              {t("hotels")}
            </Link>
            <div className="py-3">
              <button
                onClick={() => setInternationalOpen(!internationalOpen)}
                className="flex items-center justify-between w-full text-base font-medium text-gray-700 hover:text-amber-500 transition-colors"
              >
                <span>{t("international")}</span>
                <ChevronDown className="h-4 w-4" />
              </button>

              {internationalOpen && (
                <div className="mt-2 ml-4 space-y-2">
                  <Link
                    to="/dubai"
                    onClick={() => setIsOpen(false)}
                    className="block py-2 text-sm text-gray-600 hover:text-amber-500 transition-colors"
                  >
                    {t("dubai")}
                  </Link>
                  <Link
                    to="/cyprus"
                    onClick={() => setIsOpen(false)}
                    className="block py-2 text-sm text-gray-600 hover:text-amber-500 transition-colors"
                  >
                    {t("cyprus")}
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/services"
              onClick={() => setIsOpen(false)}
              className="block py-3 text-base font-medium text-gray-700 hover:text-amber-500 transition-colors"
            >
              {t("services")}
            </Link>
            <Link
              to="/contact"
              onClick={() => setIsOpen(false)}
              className="block py-3 text-base font-medium text-gray-700 hover:text-amber-500 transition-colors"
            >
              {t("contact")}
            </Link>
            <div className="py-3 border-t border-gray-100 mt-2">
              <div className="flex items-center space-x-2 mb-2 text-sm font-medium text-gray-700">
                <Globe className="h-4 w-4" />
                <span>Language</span>
              </div>
              <div className="grid grid-cols-2 gap-2 ml-6">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      setIsOpen(false);
                    }}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      language === lang.code
                        ? "bg-amber-500 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-amber-50 hover:text-amber-600"
                    }`}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
